import React, { useEffect, useReducer, useRef, useState } from 'react';
import { nextSortDirection } from '../../utils/tableSort';
import { Helmet } from 'react-helmet-async';
import { FaExchangeAlt, FaFileExcel, FaFileImport, FaFileInvoiceDollar, FaPlus } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../toasts';
import FilterForm from '../../components/FilterForm';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import MessageBox from '../../components/MessageBox';
import AssetService from '../../services/asset.service';
import { getError, showCurrency } from '../../utils';
import {
	Button,
	DataTable,
	EntityList,
	ExportModal,
	ListToolbar,
	PageHead,
	Pill,
	RowActionsMenu,
	StatStrip,
	buildXlsxExport,
} from '../../components/EntityList';
import Paginator from '../Paginator/Paginator';
import { AssetDeletionModal } from '../../components/CascadeDeletionModal';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return {
			...state,
			assets: action.payload.assets,
			allAssets: action.payload.allAssets || [],
			page: action.payload.page,
			pages: action.payload.pages,
			countAssets: action.payload.countAssets,
			pageSize: action.payload.pageSize,
			loading: false,
		};
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'DELETE_REQUEST':
		return { ...state, loadingDelete: true, successDelete: false };
	case 'DELETE_SUCCESS':
		return {
			...state,
			loadingDelete: false,
			successDelete: true,
		};
	case 'DELETE_FAIL':
		return { ...state, loadingDelete: false, successDelete: false };

	case 'DELETE_RESET':
		return { ...state, loadingDelete: false, successDelete: false };
	default:
		return state;
	}
};

const hasValue = (value) => value !== undefined && value !== null && value !== '';

// Money columns must em-dash a stored 0, not render "$U 0" — 0 reads as "no value" here.
const hasMoneyValue = (value) => hasValue(value) && Number(value) !== 0;

const renderEmpty = () => <span className='tw:text-ink-3'>—</span>;

const formatAmount = (asset, value) => {
	if (!hasMoneyValue(value)) {
		return null;
	}
	const amount = Number(value);
	if (Number.isNaN(amount)) {
		return null;
	}
	const currency = showCurrency(asset.currency) || asset.currency || '';
	return `${currency} ${amount.toLocaleString('es-UY')}`.trim();
};

// `type` is free text with no backend enum, so this grouping is a best-effort keyword
// approximation (real values seen in data: apartamento/casa/local/terreno) — not a true
// category filter. There is deliberately no "Otros" segment: it can't be expressed as a
// reliable negative match against the other two groups, and a chip that silently behaved
// like "Todos" (and could never render as selected) read as a broken filter.
const TIPO_GROUPS = {
	inmuebles: 'apartamento|casa|local',
	terrenos: 'terreno',
};

const TIPO_SEGMENTS = [
	{ label: 'Todos', value: 'all' },
	{ label: 'Inmuebles', value: 'inmuebles' },
	{ label: 'Terrenos', value: 'terrenos' },
];

function AssetList() {
	const { t } = useLabels();
	const navigate = useNavigate();
	const { canSee, canEdit, canCreate, canDelete, role } = usePermissions();
	// Same gate as the importer route (AdminRoute) and endpoints (isAdmin)
	const canImport = role === 'admin' || role === 'super_admin';

	const [{ loading, error, assets, allAssets, successDelete, pageSize, countAssets }, dispatch] =
		useReducer(reducer, {
			assets: [],
			allAssets: [],
			loading: true,
			error: '',
			countAssets: 0,
			pageSize: 0,
		});
	const initialLoading = loading && pageSize === 0;

	const { search } = useLocation();
	const sp = new URLSearchParams(search); // /search?category=Shirts
	const column = sp.get('column') || 'name';
	const direction = sp.get('direction') || 'asc';
	const currency = sp.get('currency') || 'all';
	const priceTo = sp.get('priceTo') || '';
	const priceFrom = sp.get('priceFrom') || 0;
	const query = sp.get('query') || 'all';
	const page = sp.get('page') || 1;
	const [filtered, setFiltered] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [exportModalOpen, setExportModalOpen] = useState(false);

	// cascade deletion modal
	const [assetToDelete, setAssetToDelete] = useState(null);

	const handleDropdownToggle = () => {
		setIsOpen(!isOpen);
	};

	const [filterOptions, setFilterOptions] = useState({
		formType: 'ActivosFijos',
		priceFrom: priceFrom,
		priceTo: priceTo,
		currency: currency,
		query: query,
	});

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFilterOptions((prevOptions) => ({
			...prevOptions,
			[name]: value,
			formType: filterOptions.formType,
		}));
	};

	// filterOptions seeds from the URL on mount only, while the toolbar search,
	// the Tipo chips and the paginator navigate without updating it — and
	// getFilterUrl() rebuilds every URL out of it, so a stale value dropped the
	// active search term.
	useEffect(() => {
		setFilterOptions({
			formType: 'ActivosFijos',
			priceFrom,
			priceTo,
			currency,
			query,
		});
	}, [priceFrom, priceTo, currency, query]);

	const handleApplyFilter = () => {
		const { priceFrom, priceTo, currency } = filterOptions;
		const filter = {
			priceFrom,
			priceTo,
			currency,
			page: 1,
			query,
		};
		const filterUrl = getFilterUrl(filter);
		navigate(filterUrl);
		setFiltered(true);
	};
	// Guarded like AccountList: without the ref this fired on mount and
	// immediately re-navigated, wiping column/direction off an incoming
	// (bookmarked or shared) sorted URL and costing a second fetch.
	const isMounted = useRef(false);
	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;
			return;
		}
		handleApplyFilter();
	}, [filtered]);

	const handleClearFilter = () => {
		setFilterOptions({
			formType: 'ActivosFijos',
			priceFrom: 0,
			priceTo: '',
			currency: 'all',
			query: 'all',
		});
		setFiltered(false);
	};

	// Toolbar-level "Limpiar": unlike handleClearFilter (used by the advanced-filter modal,
	// which relies on the `filtered` effect to navigate), this also navigates immediately.
	const handleClearAdvancedFilters = () => {
		setFilterOptions({
			formType: 'ActivosFijos',
			priceFrom: 0,
			priceTo: '',
			currency: 'all',
			query: 'all',
		});
		navigate(`/AdminScreen/assets/search?page=1&query=all&priceFrom=0&priceTo=&currency=all&column=${column}&direction=${direction}`);
	};

	const onPageChange = (page) => {
		navigate(getFilterUrl({ page: page }));
	};

	const handleSort = (sortColumn) => {
		const newSortDirection = nextSortDirection(column, direction, sortColumn);
		const newURL = `/AdminScreen/assets/search?page=${page}&query=${query}&column=${sortColumn}&direction=${newSortDirection}&currency=${currency}&priceFrom=${priceFrom}&priceTo=${priceTo}`;
		navigate(newURL);
	};

	// Segmented "Tipo" filter reuses the free-text `query` param (which the backend already
	// regex-matches against `type`, among other fields) — see TIPO_GROUPS note above.
	const activeTipoSegment =
		query === TIPO_GROUPS.inmuebles ? 'inmuebles' : query === TIPO_GROUPS.terrenos ? 'terrenos' : 'all';

	const handleTipoSegmentChange = (value) => {
		const groupQuery = value === 'inmuebles' ? TIPO_GROUPS.inmuebles : value === 'terrenos' ? TIPO_GROUPS.terrenos : 'all';
		setFilterOptions((prev) => ({ ...prev, query: groupQuery }));
		const params = new URLSearchParams();
		params.append('page', 1);
		params.append('query', groupQuery);
		params.append('priceFrom', priceFrom);
		params.append('priceTo', priceTo);
		params.append('currency', currency);
		params.append('column', column);
		params.append('direction', direction);
		navigate(`/AdminScreen/assets/search?${params.toString()}`);
	};

	// "Con valuación" reuses the existing priceFrom>0 range filter (already accurate server-side).
	const hasValuacionFilter = Number(priceFrom) > 0;

	const handleToggleValuacion = () => {
		const nextPriceFrom = hasValuacionFilter ? 0 : 0.01;
		setFilterOptions((prev) => ({ ...prev, priceFrom: nextPriceFrom }));
		const params = new URLSearchParams();
		params.append('page', 1);
		params.append('query', query);
		params.append('priceFrom', nextPriceFrom);
		params.append('priceTo', priceTo);
		params.append('currency', currency);
		params.append('column', column);
		params.append('direction', direction);
		navigate(`/AdminScreen/assets/search?${params.toString()}`);
	};

	const filterBadgeCount =
		(currency !== 'all' ? 1 : 0) + (Number(priceFrom) > 0 ? 1 : 0) + (priceTo !== '' ? 1 : 0);

	useEffect(() => {
		const fetchData = async () => {
			try {
				dispatch({ type: 'FETCH_REQUEST' });
				const { data } = await AssetService.search(
					`page=${page}&query=${query}&column=${column}&direction=${direction}&currency=${currency}&priceFrom=${priceFrom}&priceTo=${priceTo}`,
				);
				dispatch({ type: 'FETCH_SUCCESS', payload: data });
			} catch (err) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
				console.error(error);
				toast.error('No se pudo obtener lista');
			}
		};
		if (successDelete) {
			dispatch({ type: 'DELETE_RESET' });
		} else {
			fetchData();
		}
	}, [successDelete, column, direction, query, priceFrom, priceTo, currency, page]);

	// The cascade modal previews everything the delete takes with it.
	function deleteAssetHandler(asset) {
		setAssetToDelete(asset);
	}

	function handleAssetDeleted() {
		setAssetToDelete(null);
		handleClearFilter();
		dispatch({ type: 'DELETE_SUCCESS' });
	}

	async function editAssetHandler(asset) {
		navigate(`/AdminScreen/assets/editAsset/${asset._id}`);
	}
	async function seeDetails(asset) {
		navigate(`/AdminScreen/assets/asset/${asset._id}`);
	}
	const getFilterUrl = (filter) => {
		const { query, priceFrom, priceTo, currency } = filterOptions;
		const filterQuery = filter.query || query;
		const filterPage = filter.page || 1;
		const filterPriceFrom = priceFrom || 0;
		const filterPriceTo = priceTo || '';
		const filterCurrency = currency || '';

		const params = new URLSearchParams();
		params.append('page', filterPage);
		params.append('query', filterQuery);
		params.append('priceFrom', filterPriceFrom);
		params.append('priceTo', filterPriceTo);
		params.append('currency', filterCurrency);
		// Without these the sort silently reset to the default on every filter
		// change, search and page step.
		params.append('column', column);
		params.append('direction', direction);

		return `/AdminScreen/assets/search?${params.toString()}`;
	};
	const filterFormProps = {
		filterOptions: filterOptions,
		isOpen: isOpen,
		handleDropdownToggle: handleDropdownToggle,
		handleInputChange: handleInputChange,
		clearFilter: () => handleClearFilter(),
		handleApplyFilter: () => handleApplyFilter()
	};

	const renderMoney = (asset, value) => {
		const formatted = formatAmount(asset, value);
		return formatted ? (
			<span className='tw:font-mono tw:text-[13px] tw:tabular-nums'>{formatted}</span>
		) : (
			renderEmpty()
		);
	};

	const renderText = (value) =>
		hasValue(value) ? <span className='tw:text-[12.5px] tw:text-ink-2'>{value}</span> : renderEmpty();

	const exportFields = [
		{ key: 'name', label: t('entity.asset'), locked: true },
		{ key: 'owner.name', label: t('entity.assetOwner') },
		{ key: 'type', label: t('field.type') },
		{ key: 'ubication', label: t('field.ubication') },
		{ key: 'price', label: t('field.price') },
		{ key: 'guaranteedAmount', label: t('field.guaranteedAmount') },
		{ key: 'lot', label: t('field.lot') },
		{ key: 'folder', label: t('field.folder') },
	];

	const exportFieldGetters = {
		name: (asset) => asset.name || '',
		'owner.name': (asset) => asset.owner?.name || '',
		type: (asset) => asset.type || '',
		ubication: (asset) => asset.ubication || '',
		price: (asset) => formatAmount(asset, asset.price) || '',
		guaranteedAmount: (asset) => formatAmount(asset, asset.guaranteedAmount) || '',
		lot: (asset) => asset.lot || '',
		folder: (asset) => asset.folder || '',
	};

	// The route also returns an unpaginated `allAssets` on every request, but it
	// is built without a sort — refetching one big page keeps the sheet in the
	// same order as the table.
	async function handleExportAssets(selectedKeys) {
		const { data } = await AssetService.search(
			`page=1&query=${query}&column=${column}&direction=${direction}&currency=${currency}&priceFrom=${priceFrom}&priceTo=${priceTo}&pageSize=${countAssets || 1}`,
		);
		return buildXlsxExport({
			rows: data.assets,
			fields: exportFields,
			selectedKeys,
			getters: exportFieldGetters,
			sheetName: 'Activos',
			filePrefix: 'activos',
		});
	}

	const renderRowActions = (asset) => (
		<RowActionsMenu
			items={[
				canSee() && { label: 'Ver detalles', onClick: () => seeDetails(asset) },
				canEdit() && { label: 'Editar', onClick: () => editAssetHandler(asset) },
				canDelete() && { label: 'Eliminar', onClick: () => deleteAssetHandler(asset), danger: true },
			]}
		/>
	);

	const assetTableColumns = [
		{
			key: 'name',
			header: t('entity.asset'),
			sortable: true,
			className: 'tw:max-w-[260px]',
			render: (asset) => (
				<div>
					<div className='tw:font-medium tw:text-accent'>{asset.name || '—'}</div>
					{hasValue(asset.description) && (
						<div className='tw:text-[11.5px] tw:text-ink-3 tw:truncate' title={asset.description}>
							{asset.description}
						</div>
					)}
				</div>
			),
		},
		{
			key: 'owner.name',
			header: t('entity.assetOwner'),
			sortable: true,
			className: 'tw:max-w-[170px]',
			render: (asset) => {
				const owner = asset.owner;
				if (!owner?.name) {
					return renderEmpty();
				}
				if (!owner._id) {
					return <span className='tw:text-[12.5px] tw:text-ink-2 tw:truncate'>{owner.name}</span>;
				}
				return (
					<span
						className='tw:text-[12.5px] tw:text-ink-0 tw:truncate tw:cursor-pointer tw:hover:underline'
						onClick={(e) => {
							e.stopPropagation();
							navigate(`/AdminScreen/accounts/account/${owner._id}`);
						}}
					>
						{owner.name}
					</span>
				);
			},
		},
		{
			key: 'type',
			header: t('field.type'),
			sortable: true,
			render: (asset) => (hasValue(asset.type) ? <Pill tone='neutral'>{asset.type}</Pill> : renderEmpty()),
		},
		{
			key: 'ubication',
			header: t('field.ubication'),
			sortable: true,
			className: 'tw:max-w-[180px]',
			render: (asset) => renderText(asset.ubication),
		},
		{
			key: 'price',
			header: t('field.price'),
			sortable: true,
			className: 'tw:text-right',
			render: (asset) => renderMoney(asset, asset.price),
		},
		{
			key: 'guaranteedAmount',
			header: t('field.guaranteedAmount'),
			sortable: true,
			className: 'tw:text-right',
			render: (asset) => renderMoney(asset, asset.guaranteedAmount),
		},
		{
			key: 'lot',
			header: t('field.lot'),
			sortable: true,
			render: (asset) => (
				<div>
					{hasValue(asset.lot) ? (
						<span className='tw:font-mono tw:text-[12px] tw:text-ink-2'>{asset.lot}</span>
					) : (
						renderEmpty()
					)}
					{asset.multipleLots && (
						<div className='tw:mt-1'>
							<Pill tone='accent'>Múltiples</Pill>
						</div>
					)}
				</div>
			),
		},
		{
			key: 'folder',
			header: t('field.folder'),
			sortable: true,
			render: (asset) => renderText(asset.folder),
		},
	];

	const totalValuacion = allAssets.reduce((sum, a) => sum + (Number(a.price) || 0), 0);
	const totalGuaranteed = allAssets.reduce((sum, a) => sum + (Number(a.guaranteedAmount) || 0), 0);
	const propiedadesCount = allAssets.filter((a) => new RegExp(TIPO_GROUPS.inmuebles, 'i').test(a.type || '')).length;
	const guaranteedCount = allAssets.filter((a) => Number(a.guaranteedAmount) > 0).length;
	const conPadronCount = allAssets.filter((a) => hasValue(a.lot)).length;
	const multiplesCount = allAssets.filter((a) => a.multipleLots).length;

	const statStripStats = [
		{
			label: 'Valuación total',
			value: totalValuacion.toLocaleString('es-UY'),
			mono: true,
			accent: '--color-cat-4',
			sub: `${allAssets.length} activos`,
		},
		{
			label: 'Activos registrados',
			value: countAssets,
			sub: `${propiedadesCount} propiedades`,
		},
		{
			label: 'Monto garantizado',
			value: totalGuaranteed.toLocaleString('es-UY'),
			mono: true,
			sub: `${guaranteedCount} con garantía`,
		},
		{
			label: 'Con padrón',
			value: conPadronCount,
			sub: `${multiplesCount} múltiples`,
		},
	];

	return error ? (
		<MessageBox variant='danger'>{error}</MessageBox>
	) : (
		<div>
			<Helmet>
				<title>{`Lista de ${t('entity.asset.plural')}`}</title>
			</Helmet>

			<AssetDeletionModal
				show={Boolean(assetToDelete)}
				record={assetToDelete}
				onClose={() => setAssetToDelete(null)}
				onDeleted={handleAssetDeleted}
			/>

			<FilterForm {...filterFormProps} />

			<EntityList>
				<PageHead
					title={t('entity.asset.plural')}
					subtitle={`${initialLoading ? '—' : countAssets} ${t('entity.asset.plural').toLowerCase()} encontrados`}
					actions={
						<>
							<Button variant='default' icon={<FaFileExcel />} onClick={() => setExportModalOpen(true)}>
								Exportar
							</Button>
							{canImport && (
								<Button variant='default' icon={<FaFileImport />} onClick={() => navigate('/AdminScreen/assets/importar-gastos')}>
									{t('import.billsPageTitle')}
								</Button>
							)}
							{canCreate() && (
								<Button
									icon={<FaFileInvoiceDollar />}
									onClick={() => navigate('/AdminScreen/newBill', { state: { returnTo: '/AdminScreen/assets' } })}
								>
									{`Agregar ${t('entity.bill')}`}
								</Button>
							)}
							{canCreate() && (
								<Button
									icon={<FaExchangeAlt />}
									onClick={() => navigate('/AdminScreen/newAssetSale', { state: { returnTo: '/AdminScreen/assets' } })}
								>
									{`Agregar ${t('entity.assetSale')}`}
								</Button>
							)}
							{canCreate() && (
								<Button variant='primary' icon={<FaPlus />} onClick={() => navigate('/AdminScreen/newAsset')}>
									Agregar Activo
								</Button>
							)}
						</>
					}
				/>
				<div className='tw:mb-5'>
					<StatStrip stats={statStripStats} loading={initialLoading} />
				</div>
				<ListToolbar
					query={query}
					getFilterUrl={getFilterUrl}
					navigate={navigate}
					searchPlaceholder='Buscar por activo, Padrón, propietario, tipo o ubicación…'
					segments={TIPO_SEGMENTS}
					activeSegment={activeTipoSegment}
					onSegmentChange={handleTipoSegmentChange}
					chipFilter={{ label: 'Con valuación', active: hasValuacionFilter, onToggle: handleToggleValuacion }}
					onAdvancedFilter={handleDropdownToggle}
					filterBadgeCount={filterBadgeCount}
					onClearFilters={filterBadgeCount > 0 ? handleClearAdvancedFilters : undefined}
					resultCount={initialLoading ? null : countAssets}
				/>
				<DataTable
					columns={assetTableColumns}
					rows={assets}
					loading={loading}
					skeleton
					skeletonRows={pageSize || 15} // 15 matches routes/assetRoutes.js PAGE_SIZE
					error={error}
					sortColumn={column}
					direction={direction}
					onSort={handleSort}
					onRowClick={(asset) => seeDetails(asset)}
					rowActions={canEdit() || canDelete() ? renderRowActions : undefined}
					emptyMessage='No se encontraron activos con los filtros aplicados.'
				/>
				<Paginator
					className='tw:mt-4'
					totalCount={countAssets}
					pageSize={pageSize}
					currentPage={parseInt(page)}
					onPageChange={onPageChange}
				/>
			</EntityList>

			<ExportModal
				show={exportModalOpen}
				onClose={() => setExportModalOpen(false)}
				count={countAssets}
				fields={exportFields}
				subtitle={`${t('entity.asset.plural')} — se exportará el listado filtrado actual`}
				onExport={handleExportAssets}
			/>
		</div>
	);
}

export default AssetList;
